import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  AcademicCapIcon,
  LightBulbIcon,
  ChartBarIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  SparklesIcon,
  BookOpenIcon,
  CogIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const ConsultingServices = () => {
  const { t } = useTranslation();

  const services = [
    {
      id: 1,
      icon: AcademicCapIcon,
      title: t('consultingServices.laboratory.title'),
      description: t('consultingServices.laboratory.description'),
      features: [
        t('consultingServices.laboratory.feature1'),
        t('consultingServices.laboratory.feature2'),
        t('consultingServices.laboratory.feature3'),
        t('consultingServices.laboratory.feature4')
      ]
    },
    {
      id: 2,
      icon: ChartBarIcon,
      title: t('consultingServices.quality.title'),
      description: t('consultingServices.quality.description'),
      features: [
        t('consultingServices.quality.feature1'),
        t('consultingServices.quality.feature2'),
        t('consultingServices.quality.feature3'),
        t('consultingServices.quality.feature4')
      ]
    },
    {
      id: 3,
      icon: CogIcon,
      title: t('consultingServices.operations.title'),
      description: t('consultingServices.operations.description'),
      features: [
        t('consultingServices.operations.feature1'),
        t('consultingServices.operations.feature2'),
        t('consultingServices.operations.feature3'),
        t('consultingServices.operations.feature4')
      ]
    },
    {
      id: 4,
      icon: UserGroupIcon,
      title: t('consultingServices.training.title'),
      description: t('consultingServices.training.description'),
      features: [
        t('consultingServices.training.feature1'),
        t('consultingServices.training.feature2'),
        t('consultingServices.training.feature3'),
        t('consultingServices.training.feature4')
      ]
    }
  ];

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: t('consultingServices.benefits.expertise.title'),
      description: t('consultingServices.benefits.expertise.description')
    },
    {
      icon: ClockIcon,
      title: t('consultingServices.benefits.time.title'),
      description: t('consultingServices.benefits.time.description')
    },
    {
      icon: CheckCircleIcon,
      title: t('consultingServices.benefits.results.title'),
      description: t('consultingServices.benefits.results.description')
    },
    {
      icon: SparklesIcon,
      title: t('consultingServices.benefits.innovation.title'),
      description: t('consultingServices.benefits.innovation.description')
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-brand-blue via-brand-blueSecondary to-brand-green text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-white/10 rounded-full backdrop-blur-sm">
                <LightBulbIcon className="w-16 h-16" />
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              {t('consultingServices.hero.title')}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              {t('consultingServices.hero.subtitle')}
            </p>
            <div className="bg-yellow-500 text-yellow-900 px-6 py-3 rounded-full inline-block font-semibold text-lg">
              {t('consultingServices.hero.comingSoon')}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('consultingServices.services.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('consultingServices.services.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-center mb-6">
                  <div className="p-3 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-lg">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mr-4">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700">
                      <CheckCircleIcon className="w-5 h-5 text-green-500 ml-3 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('consultingServices.benefits.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('consultingServices.benefits.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="p-4 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('consultingServices.process.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('consultingServices.process.subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-8">
            {[1, 2, 3, 4].map((step, index) => (
              <motion.div
                key={step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center relative"
              >
                <div className="w-16 h-16 bg-gradient-to-r from-brand-blue to-brand-blueSecondary rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4">
                  {step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {t(`consultingServices.process.step${step}.title`)}
                </h3>
                <p className="text-gray-600">
                  {t(`consultingServices.process.step${step}.description`)}
                </p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 -left-8 w-16 h-0.5 bg-gray-300"></div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-brand-blue to-brand-green text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <SparklesIcon className="w-16 h-16 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">
              {t('consultingServices.cta.title')}
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              {t('consultingServices.cta.subtitle')}
            </p>
            <div className="bg-yellow-500 text-yellow-900 px-8 py-4 rounded-full inline-block font-bold text-xl">
              {t('consultingServices.cta.comingSoon')}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ConsultingServices;
